import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { PRODUCTS } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeaveReview() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("order") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [productId, setProductId] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) { toast.error("Enter your Order ID."); return; }
    if (!email.trim()) { toast.error("Enter the email you used at checkout."); return; }
    if (!productId) { toast.error("Pick the product you bought."); return; }
    if (!rating) { toast.error("Tap the stars to rate it."); return; }
    if (text.trim().length < 3) { toast.error("Write a few words about the piece."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        order_id: orderId.trim(),
        email: email.trim(),
        product_id: productId,
        rating,
        text: text.trim(),
      });
      setDone(productId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit review. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="max-w-xl mx-auto px-6 py-28 text-center" data-testid="review-success">
        <SEO title="Review Submitted" />
        <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <CheckCircle2 size={52} className="mx-auto text-emerald-400" />
          <h1 className="font-serif text-4xl mt-4">Review live.</h1>
          <p className="text-white/60 mt-3 text-sm">Thanks for the words — it's already on the product page.</p>
          <Link to={`/product/${done}`} data-testid="view-review-on-product" className="mt-8 inline-block bg-white text-black font-mono-cap text-xs px-8 py-4 hover:bg-[#FF69B4] hover:text-white transition-colors">
            See it on the product →
          </Link>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="max-w-xl mx-auto px-5 sm:px-6 py-16 sm:py-24">
      <SEO title="Leave a Review" description="Rate your Dripchexk piece using your Order ID." />
      <p className="font-mono-cap text-[10px] text-[#FF69B4] tracking-[0.3em]">THE DRIP SPEAKS</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-2">Leave a review.</h1>
      <p className="text-white/50 text-sm mt-3">Your Order ID is on your confirmation screen and email (looks like TDC1005). No login needed.</p>

      <form onSubmit={submit} className="mt-10 space-y-6">
        <div>
          <label className="font-mono-cap text-[10px] text-white/40 block mb-2">Order ID</label>
          <input
            data-testid="review-order-input"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="TDC1005"
            className="w-full bg-[#0e0e0e] border border-white/15 px-4 py-3.5 text-sm focus:border-white outline-none uppercase placeholder:normal-case"
          />
        </div>

        <div>
          <label className="font-mono-cap text-[10px] text-white/40 block mb-2">Checkout Email</label>
          <input
            data-testid="review-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="The email you used when ordering"
            className="w-full bg-[#0e0e0e] border border-white/15 px-4 py-3.5 text-sm focus:border-white outline-none"
          />
        </div>

        <div>
          <label className="font-mono-cap text-[10px] text-white/40 block mb-2">Product</label>
          <select
            data-testid="review-product-select"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="w-full bg-[#0e0e0e] border border-white/15 px-4 py-3.5 text-sm focus:border-white outline-none appearance-none"
          >
            <option value="">Select the piece you bought…</option>
            {PRODUCTS.map((p) => (
              <option key={p.slug} value={p.slug}>{p.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="font-mono-cap text-[10px] text-white/40 block mb-2">Rating</label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`review-star-${n}`}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHoverStar(n)}
                onMouseLeave={() => setHoverStar(0)}
                className="p-1"
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
              >
                <Star
                  size={28}
                  className={(hoverStar || rating) >= n ? "text-[#FF69B4] fill-[#FF69B4]" : "text-white/25"}
                />
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="font-mono-cap text-[10px] text-white/40 block mb-2">Your review</label>
          <textarea
            data-testid="review-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            maxLength={1000}
            placeholder="How's the fit, the fabric, the drip?"
            className="w-full bg-[#0e0e0e] border border-white/15 px-4 py-3.5 text-sm focus:border-white outline-none resize-none"
          />
        </div>

        <button
          type="submit"
          data-testid="review-submit-btn"
          disabled={submitting}
          className="w-full bg-white text-black font-mono-cap text-xs py-4 hover:bg-[#FF69B4] hover:text-white transition-colors disabled:opacity-50"
        >
          {submitting ? "Submitting…" : "Submit Review"}
        </button>
      </form>
    </section>
  );
}
